import { ContainerModule } from "inversify";
import { combineLatest, filter, share, Subscription, switchMap, tap } from "rxjs";

import { AbilityService, Ability_DIID, Ability_Service_DIID } from "@archipad-js/core/ability";
import { DIID } from "@archipad-js/dependency-injection";

import _LegacyServiceManager from "@core/services/legacy-service-manager";

import { currentProject$ } from "@archipad/backend/project/baseProjectContext";
import { getAccountService } from '@archipad/services/account.service';

import { computeArchipadAbilityRules, makeArchipadAbility } from "./archipad-ability.factory";
import { ArchipadAbility } from "@archipad-js/archipad/entity-services";

export const Archipad_Ability_DIID = DIID<ArchipadAbility>("ArchipadAbility");
export const Archipad_Ability_Service_DIID = DIID<AbilityService<ArchipadAbility>>("ArchipadAbilityService");

export function getArchipadAbilityService(): AbilityService<ArchipadAbility> {
    return _LegacyServiceManager.container.get(Archipad_Ability_Service_DIID.symbol);
}

export const ArchipadAbilityModule = new ContainerModule(
    (bind, unbind, isBound, rebind, unbindAsync, onActivation, onDeactivation) => {
        bind(Ability_Service_DIID.symbol).to(AbilityService).inSingletonScope();

        // rebind the ability to the archipad ability
        bind(Archipad_Ability_DIID.symbol).toDynamicValue((context) => {
            return context.container.get(Ability_DIID.symbol) as ArchipadAbility;
        });

        // rebind the ability service to the archipad ability service
        bind(Archipad_Ability_Service_DIID.symbol).toDynamicValue((context) => {
            return context.container.get(Ability_Service_DIID.symbol) as AbilityService<ArchipadAbility>;
        });

        // do not recreate the ability if it already exists (e.g. in tests)
        if (isBound(Ability_DIID.symbol)) {
            return;
        }

        const archipadAbility = makeArchipadAbility();
        const abilityBinding = bind(Ability_DIID.symbol).toConstantValue(archipadAbility);

        const subscription = new Subscription();

        const abilityUpdates$ = _LegacyServiceManager.isReady$.pipe(
            filter( isReady => isReady === true),
            switchMap((e) => {
                return combineLatest([getAccountService().currentUserInformation$, getAccountService().currentPlan$, currentProject$]).pipe(
                    tap(([authInfo, accountInfo, project]) => {
                        archipadAbility.update(computeArchipadAbilityRules(authInfo, accountInfo, project));
                    }),
                    share(),
                )
            }),
        );

        abilityBinding.onActivation((context, ability) => {
            subscription.add(abilityUpdates$.subscribe());
            return ability;
        });

        abilityBinding.onDeactivation(() => {
            subscription.unsubscribe();
        });
    },
);
